class LuxuryCaseShare extends HTMLElement {
  connectedCallback() {
    this.autoplay = this.dataset.autoplay === 'true';
    this.intervalMs = Number(this.dataset.interval || 7) * 1000;
    this.enableAnimation = this.dataset.enableAnimation === 'true';

    this.desktopRoot = this.querySelector('[data-lcs-desktop]');
    this.pages = Array.from(this.querySelectorAll('[data-lcs-page]'));
    this.dotsWrap = this.querySelector('[data-lcs-dots]');

    this.mobileTrack = this.querySelector('[data-lcs-mobile-track]');
    this.mobileSlides = Array.from(this.querySelectorAll('[data-lcs-mobile-slide]'));
    this.mobileDotsWrap = this.querySelector('[data-lcs-mobile-dots]');
    this.mobileIndex = 0;

    this.index = 0;
    this.isInView = false;
    this.timer = null;

    this.onPrev = () => this.go(this.index - 1);
    this.onNext = () => this.go(this.index + 1);
    this.onVisibility = this.handleVisibility.bind(this);
    this.onMobileScroll = this.handleMobileScroll.bind(this);
    this.pause = () => this.stopAutoplay();
    this.resume = () => {
      if (this.isInView) this.startAutoplay();
    };

    this.querySelector('[data-lcs-prev]')?.addEventListener('click', this.onPrev);
    this.querySelector('[data-lcs-next]')?.addEventListener('click', this.onNext);
    document.addEventListener('visibilitychange', this.onVisibility);
    this.desktopRoot?.addEventListener('mouseenter', this.pause);
    this.desktopRoot?.addEventListener('mouseleave', this.resume);
    this.mobileTrack?.addEventListener('scroll', this.onMobileScroll, { passive: true });

    this.buildDots();
    this.buildMobileDots();
    this.setupInViewObserver();

    if (!('IntersectionObserver' in window)) {
      this.isInView = true;
      window.requestAnimationFrame(() => this.playEntrance(0));
    }
  }

  disconnectedCallback() {
    this.stopAutoplay();
    this.inViewObserver?.disconnect();
    this.querySelector('[data-lcs-prev]')?.removeEventListener('click', this.onPrev);
    this.querySelector('[data-lcs-next]')?.removeEventListener('click', this.onNext);
    document.removeEventListener('visibilitychange', this.onVisibility);
    this.desktopRoot?.removeEventListener('mouseenter', this.pause);
    this.desktopRoot?.removeEventListener('mouseleave', this.resume);
    this.mobileTrack?.removeEventListener('scroll', this.onMobileScroll);
  }

  setupInViewObserver() {
    if (!('IntersectionObserver' in window)) {
      this.isInView = true;
      return;
    }

    this.inViewObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const wasInView = this.isInView;
          this.isInView = entry.isIntersecting;

          if (entry.isIntersecting && !wasInView) {
            this.playEntrance(this.index);
            this.startAutoplay();
          }

          if (!entry.isIntersecting && wasInView) {
            this.stopAutoplay();
          }
        });
      },
      { threshold: 0.2 }
    );

    this.inViewObserver.observe(this);
  }

  buildDots() {
    if (!this.dotsWrap || this.pages.length <= 1) return;
    this.dotsWrap.replaceChildren();

    for (let i = 0; i < this.pages.length; i += 1) {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = `lcs__dot${i === 0 ? ' is-active' : ''}`;
      button.setAttribute('aria-label', `Go to slide ${i + 1}`);
      button.addEventListener('click', () => this.go(i));
      this.dotsWrap.appendChild(button);
    }
  }

  buildMobileDots() {
    if (!this.mobileDotsWrap || this.mobileSlides.length <= 1) return;
    this.mobileDotsWrap.replaceChildren();

    for (let i = 0; i < this.mobileSlides.length; i += 1) {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = `lcs__dot${i === 0 ? ' is-active' : ''}`;
      button.setAttribute('aria-label', `Go to case ${i + 1}`);
      button.addEventListener('click', () => this.goMobile(i));
      this.mobileDotsWrap.appendChild(button);
    }
  }

  setActiveDots(wrap, index) {
    if (!wrap) return;
    wrap.querySelectorAll('.lcs__dot').forEach((dot, i) => {
      dot.classList.toggle('is-active', i === index);
    });
  }

  goMobile(index) {
    if (!this.mobileTrack || !this.mobileSlides.length) return;
    const next = Math.max(0, Math.min(index, this.mobileSlides.length - 1));
    const slide = this.mobileSlides[next];
    this.mobileTrack.scrollTo({ left: slide.offsetLeft, behavior: 'smooth' });
    this.mobileIndex = next;
    this.setActiveDots(this.mobileDotsWrap, next);
  }

  handleMobileScroll() {
    if (!this.mobileTrack || !this.mobileSlides.length) return;
    const slideWidth = this.mobileSlides[0].offsetWidth || 1;
    const gap = 14;
    const index = Math.round(this.mobileTrack.scrollLeft / (slideWidth + gap));
    const clamped = Math.max(0, Math.min(index, this.mobileSlides.length - 1));
    if (clamped === this.mobileIndex) return;
    this.mobileIndex = clamped;
    this.setActiveDots(this.mobileDotsWrap, clamped);
  }

  beginEntrance(page) {
    if (!this.enableAnimation) return;
    page.classList.add('is-replaying');
    void page.offsetHeight;

    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => {
        page.classList.remove('is-replaying');
      });
    });
  }

  playEntrance(index) {
    if (!this.pages.length) return;

    const next = (index + this.pages.length) % this.pages.length;
    const target = this.pages[next];
    if (!target) return;

    if (target.classList.contains('is-active')) {
      this.beginEntrance(target);
      this.index = next;
      this.setActiveDots(this.dotsWrap, next);
      this.startAutoplay();
      return;
    }

    target.setAttribute('aria-hidden', 'false');
    target.classList.add('is-active');
    if (this.enableAnimation) this.beginEntrance(target);

    this.pages.forEach((page, i) => {
      if (i === next) return;
      page.classList.remove('is-active', 'is-replaying');
      page.setAttribute('aria-hidden', 'true');
    });

    this.index = next;
    this.setActiveDots(this.dotsWrap, next);
    this.startAutoplay();
  }

  go(index) {
    if (!this.pages.length) return;
    const next = (index + this.pages.length) % this.pages.length;
    this.playEntrance(next);
  }

  startAutoplay() {
    this.stopAutoplay();
    if (!this.autoplay || this.pages.length <= 1 || !this.isInView || document.hidden) return;
    this.timer = window.setInterval(() => this.go(this.index + 1), this.intervalMs);
  }

  stopAutoplay() {
    if (!this.timer) return;
    window.clearInterval(this.timer);
    this.timer = null;
  }

  handleVisibility() {
    if (document.hidden) {
      this.stopAutoplay();
      return;
    }
    if (this.isInView) {
      this.playEntrance(this.index);
      this.startAutoplay();
    }
  }
}

if (!customElements.get('luxury-case-share')) {
  customElements.define('luxury-case-share', LuxuryCaseShare);
}
